// Firebase Configuration
import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Skip the hard failure in test environments — unit tests mock Firebase and
// don't need real credentials. Vitest sets MODE='test' and VITEST=true.
const isTestEnv =
  import.meta.env.MODE === 'test' ||
  (typeof process !== 'undefined' && process.env?.VITEST);

// Firebase configuration from environment variables. In tests, fall back to
// dummy values so `getAuth`/`getFirestore` don't throw `auth/invalid-api-key`
// — tests mock the SDK anyway and never hit the network.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (isTestEnv ? 'test-api-key' : undefined),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || (isTestEnv ? 'test.firebaseapp.com' : undefined),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || (isTestEnv ? 'test-project' : undefined),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || (isTestEnv ? 'test-app-id' : undefined),
};

// Validate required env vars up-front so a misconfigured deploy fails with a
// clear, actionable error — not a cascade of `Cannot read properties of
// undefined` TypeErrors from downstream Firestore/Auth/Functions callers.
const REQUIRED_ENV_VARS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

const missingVars = REQUIRED_ENV_VARS.filter(
  (name) => !import.meta.env[name as keyof ImportMetaEnv]
);

if (missingVars.length > 0 && !isTestEnv) {
  throw new Error(
    `[Firebase] Missing required env vars: ${missingVars.join(', ')}. ` +
      `Check your .env / hosting build-time configuration.`
  );
}

// Initialize Firebase — let real init errors propagate to ErrorBoundary / Sentry
// instead of swallowing them and leaving auth/db/functions as undefined.
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app);

// Emulator connection is best-effort — if the emulators aren't running the
// app should still load against production Firebase.
console.log('🔍 Checking emulator setting:', import.meta.env.VITE_USE_FIREBASE_EMULATOR);
if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8081);
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    console.log('🔧 Connected to Firebase Emulators (Firestore, Auth, Functions)');
  } catch (error) {
    console.warn('⚠️ Failed to connect to Firebase emulators:', error);
  }
} else {
  console.log('⚠️ NOT using emulators - VITE_USE_FIREBASE_EMULATOR is:', import.meta.env.VITE_USE_FIREBASE_EMULATOR);
}

console.log('✅ Firebase initialized successfully');

export { db, auth, functions };
export default app;
